import type { MediaItemInterface } from '@hypercodestudio/basler-components/dist/components/elements/media/Media.vue';
import type { MediaImageInterface } from '@hypercodestudio/basler-components/dist/components/elements/media/types/MediaImage.vue';
import { objectEntries } from '~/utils/objectEntries';
import { isDefined } from '~/utils/guards/isDefined';
import type { ImageFocalPointValue } from '~/types/ImageFocalPoint';

type AspectRatio = [x: number, y: number];
type FitOptions = 'pad' | 'fill' | 'scale' | 'crop' | 'thumb';

const fallbackWidth = 800;
const fallbackFormat = 'webp';
const widths = [400, 800, 1400, 2800];
const defaultQuality = 80;
const defaultAspectRatio: AspectRatio = [16, 9];
const defaultFit: FitOptions = 'pad';

export type GenerateImageObjectOptions = {
  keepAspectRatio?: boolean;
  width?: number;
  height?: number;
  sizes?: string;
  fit?: FitOptions;
  widths?: ReadonlyArray<number>;
  quality?: number;
  bgColor?: string;
  format?: 'webp' | 'avif' | 'jpg';
  aspectRatio?: [16, 9] | [4, 3];
};

/**
 * XXX: make webapp version independent from GenerateImageObjectIImage
 */
export function generateImageObject(
  image?: {
    url: string;
    width?: number;
    height?: number;
    caption?: string;
    altTag?: string;
  },
  options: GenerateImageObjectOptions = {}
): MediaItemInterface | undefined {
  const imageBaseUrl = image?.url;
  if (!imageBaseUrl) {
    return;
  }

  const imageWidth = image?.width;
  const imageHeight = image?.height;
  const filteredWidths = options.widths ?? [];
  const imageArea = 'center';

  return {
    content: {
      src: buildImageSrc({
        baseUrl: imageBaseUrl,
        format: options.format ?? fallbackFormat,
        imageArea,
        width: options?.width ?? fallbackWidth,
        height: options?.height,
        quality: options.quality ?? defaultQuality,
        aspectRatio:
          options?.keepAspectRatio === true
            ? undefined
            : options.aspectRatio ?? defaultAspectRatio,
        fit: options.fit ?? defaultFit,
        bgColor: options.bgColor
      }),
      ...(filteredWidths && {
        srcset: filteredWidths
          .map(
            (width) =>
              buildImageSrc({
                baseUrl: imageBaseUrl,
                format: 'webp',
                imageArea,
                width,
                quality: options.quality ?? defaultQuality,
                aspectRatio:
                  options?.keepAspectRatio === true
                    ? undefined
                    : options.aspectRatio ?? defaultAspectRatio,
                fit: options.fit ?? defaultFit,
                bgColor: options.bgColor
              }) + ` ${width}w`
          )
          .join(', ')
      }),
      alt: image.altTag ?? '',
      width: imageWidth,
      height: imageHeight
    } satisfies MediaImageInterface,
    caption: image?.caption ?? '',
    type: 'image'
  };
}

type ImageSrcOptions = {
  baseUrl: string;
  format?: 'webp' | 'avif' | 'jpg';
  imageArea?: ImageFocalPointValue;
  width: number;
  height?: number;
  quality?: number;
  aspectRatio?: AspectRatio;
  fit?: FitOptions;
  bgColor?: string;
};

const imageParameterMap: Record<
  Exclude<keyof ImageSrcOptions, 'baseUrl' | 'aspectRatio'> | 'height',
  string
> = {
  format: 'fm',
  imageArea: 'f',
  width: 'w',
  height: 'h',
  quality: 'q',
  fit: 'fit',
  bgColor: 'bg'
};

export function buildImageSrcWithDefaults({
  baseUrl,
  format = 'webp',
  imageArea = 'center',
  width,
  quality = defaultQuality,
  aspectRatio = [16, 9],
  fit = defaultFit,
  bgColor
}: ImageSrcOptions) {
  return buildImageSrc({
    baseUrl,
    format,
    imageArea,
    width,
    quality,
    aspectRatio,
    fit,
    bgColor
  });
}

function buildImageSrc({
  baseUrl,
  format,
  imageArea,
  width,
  height,
  quality,
  aspectRatio,
  fit,
  bgColor
}: ImageSrcOptions) {
  const usedHeight =
    height ??
    (aspectRatio && getHeightFromWidthAndAspectRatio(width, aspectRatio));

  const options = {
    format,
    imageArea,
    width,
    height: usedHeight,
    quality,
    fit,
    bgColor
  };

  const query = objectEntries(options)
    .filter(([, value]) => isDefined(value))
    .map(([key, value]) => `${imageParameterMap[key]}=${value}`)
    .join('&');

  return `${baseUrl}?${query}`;
}

export function getHeightFromWidthAndAspectRatio(
  width: number,
  aspectRatio: AspectRatio
) {
  return Math.round(width * (aspectRatio[1] / aspectRatio[0]));
}

export type GenerateImageObjectIImage = {
  fields: {
    image: {
      fields: {
        file: {
          url: string;
          details: {
            image?: {
              width: number;
              height: number;
            };
          };
        };
      };
    };
    focalPoint: Record<string, any>;
    altTag?: string;
    caption?: string;
  };
};
